/**
 * 觀眾端 TTS 播放自癒邏輯：手機鎖屏或切到背景 APP 時，OS 只會強制 <audio> 進入 paused，
 * 不會發出 ended 或 error 事件，讓「播放中」旗標卡住、輪詢自癒判斷失準。
 * 這裡把判斷邏輯拆成不依賴 Vue 元件狀態的純函式，方便獨立測試：
 * makeStuckPlaybackWatchdog 累積連續卡死次數避免單次抖動誤判、
 * buildVisibilityResumePlan 規劃回前景當下要執行的恢復動作、
 * makeHeadOfLineSkipper 偵測隊首訊息音檔遲遲生不出來時該跳過、
 * buildStuckRecoveryAction 判定卡死當下該原地續播還是交還輪詢排程。
 */

/**
 * @interface {StuckPlaybackState} 卡死播放狀態的輸入快照
 */
export interface StuckPlaybackState {
	/**
	 * 目前記錄的播放中旗標
	 */
	isVoicePlaying: boolean
	/**
	 * <audio> 元素目前是否為 paused
	 */
	playerPaused: boolean
	/**
	 * <audio> 元素目前是否為 ended
	 */
	playerEnded: boolean
}

/**
 * 建立卡死播放看門狗：連續 thresholdTicks 次偵測到「旗標為播放中但實際上 player 已停」才回報卡死，
 * 避免 play() Promise 尚未 resolve 前的短暫 paused 被誤判
 *
 * @param thresholdTicks {number} 連續判定為卡死需要的 tick 次數
 * @returns {{ tick(state: StuckPlaybackState): boolean; reset(): void }} 每次輪詢呼叫一次 tick，回傳是否應該解除卡死旗標；reset 捨棄已累積的連續計數
 *
 * @example
 * const watchdog = makeStuckPlaybackWatchdog(3)
 * if (watchdog.tick({ isVoicePlaying: true, playerPaused: true, playerEnded: false })) {
 *   setIsVoicePlaying(false)
 * }
 */
export const makeStuckPlaybackWatchdog = (
	thresholdTicks: number,
): { tick: (state: StuckPlaybackState) => boolean; reset: () => void } => {
	// 存活在 closure 跨多次 tick 累加，才能判斷連續卡死而非單次抖動
	let consecutiveStuckCount = 0

	return {
		tick: (state: StuckPlaybackState): boolean => {
			// 本次 tick 判定：旗標仍顯示播放中，但 player 實際已進入 paused 或 ended
			const isStuckThisTick =
				state.isVoicePlaying &&
				(state.playerPaused || state.playerEnded)
			// 本次未偵測到卡死
			if (!isStuckThisTick) {
				// 歸零讓下次卡死須重新累積，避免時好時壞的抖動被誤判為連續卡死
				consecutiveStuckCount = 0
				// 尚未卡死，不需回報
				return false
			}

			// 疑似卡死，先累加次數，等連續達門檻才確認
			consecutiveStuckCount += 1
			// 連續次數還沒到門檻
			if (consecutiveStuckCount < thresholdTicks) {
				// 再觀察下一輪，排除單次抖動造成的誤判
				return false
			}

			// 已達門檻，回報卡死並歸零計數，避免下次立即又回 true
			consecutiveStuckCount = 0
			return true
		},
		reset: (): void => {
			// 回前景時呼叫，清空背景期間累積的計數，避免帶著過期的誤判基礎回到前景
			consecutiveStuckCount = 0
		},
	}
}

/**
 * @interface {VisibilityResumeInput} 規劃頁面回前景時的 TTS 恢復動作輸入
 */
export interface VisibilityResumeInput {
	/**
	 * 目前是否為靜音狀態
	 */
	isMuted: boolean
	/**
	 * 目前是否有任何訊息
	 */
	hasMessages: boolean
	/**
	 * 最新一筆訊息的 index
	 */
	latestIndex: number
	/**
	 * player 是否仍在正常播放中（有音源且未 paused、未 ended）
	 */
	playerActive: boolean
	/**
	 * 佇列是否已播完（目前 index 已越過最後一筆訊息）
	 */
	queueDrained: boolean
}

/**
 * @interface {VisibilityResumePlan} 頁面回前景時要執行的 TTS 恢復動作規劃
 */
export interface VisibilityResumePlan {
	/**
	 * 隱藏期間輪詢重入鎖可能卡在 true，回前景時清掉才能讓下一輪輪詢正常執行
	 */
	resetPollingLock: boolean
	/**
	 * 隱藏期間看門狗未執行、卡死計數視為過期，回前景時清掉播放中旗標避免帶著舊誤判卡住
	 */
	resetPlayingFlag: boolean
	/**
	 * 需要強制跳轉到的訊息 index；null 表示維持原本播放位置，不打斷或重播
	 */
	jumpToIndex: number | null
	/**
	 * 是否要跳過等下一輪輪詢，立即搶播（用於從暫停或已播完狀態回前景的情境）
	 */
	shouldTryPlay: boolean
	/**
	 * 輪詢 interval 隱藏期間可能已被清除，回前景時是否需要補建才能繼續偵測
	 */
	ensureInterval: boolean
}

/**
 * 依目前狀態規劃頁面回前景時的 TTS 恢復動作；對齊 mute→unmute 的既有語義（跳到最新一筆）
 *
 * @param input {VisibilityResumeInput} 回前景當下的靜音 / 訊息 / player / 佇列狀態
 * @returns {VisibilityResumePlan} 呼叫端應該依此執行的恢復動作
 *
 * @example
 * const plan = buildVisibilityResumePlan({ isMuted: false, hasMessages: true, latestIndex: 7, playerActive: false, queueDrained: false })
 * if (plan.jumpToIndex !== null) currentVoiceIndex.value = plan.jumpToIndex
 */
export const buildVisibilityResumePlan = (
	input: VisibilityResumeInput,
): VisibilityResumePlan => {
	// 靜音狀態：只重置輪詢鎖，其餘播放相關動作全部不動
	if (input.isMuted) {
		return {
			resetPollingLock: true,
			resetPlayingFlag: false,
			jumpToIndex: null,
			shouldTryPlay: false,
			ensureInterval: false,
		}
	}

	// 非靜音且 player 仍在正常播放中：不打斷現有播放，只確保輪詢 interval 存在
	if (input.playerActive) {
		return {
			resetPollingLock: true,
			resetPlayingFlag: false,
			jumpToIndex: null,
			shouldTryPlay: false,
			ensureInterval: true,
		}
	}

	// 非靜音、player 已停、且佇列已播完：只重置卡死旗標並確保輪詢，不跳回最後一筆重播
	if (input.queueDrained) {
		return {
			resetPollingLock: true,
			resetPlayingFlag: true,
			jumpToIndex: null,
			shouldTryPlay: false,
			ensureInterval: true,
		}
	}

	// 非靜音且 player 已停（被 OS 暫停）：重置卡死旗標，並視是否有訊息決定要不要跳到最新一筆搶播
	return {
		resetPollingLock: true,
		resetPlayingFlag: true,
		jumpToIndex: input.hasMessages ? input.latestIndex : null,
		shouldTryPlay: input.hasMessages,
		ensureInterval: true,
	}
}

/**
 * @interface {HeadOfLineTickInput} head-of-line 逾時偵測的輸入快照
 */
export interface HeadOfLineTickInput {
	/**
	 * 目前隊首訊息的 index，無合法目標時為 null
	 */
	index: number | null
	/**
	 * 隊首訊息是否已有可播放的音檔 URL
	 */
	hasAudioUrl: boolean
	/**
	 * 目前時間戳（ms）
	 */
	nowMs: number
}

/**
 * 建立 head-of-line 逾時偵測器：同一個 index 持續等不到音檔超過 timeoutMs 就回報應該跳過，
 * 避免卡在一筆音檔永遠生不出來的訊息，讓後續訊息全部堵住
 *
 * @param timeoutMs {number} 同一 index 允許等待音檔的最長時間
 * @returns {{ tick(input: HeadOfLineTickInput): boolean; reset(): void }} 每次輪詢呼叫一次 tick，回傳是否應該跳過目前 index；reset 捨棄已追蹤的 index 與起算時間
 *
 * @example
 * const skipper = makeHeadOfLineSkipper(15000)
 * if (skipper.tick({ index: currentVoiceIndex.value, hasAudioUrl: false, nowMs: Date.now() })) {
 *   currentVoiceIndex.value += 1
 * }
 */
export const makeHeadOfLineSkipper = (
	timeoutMs: number,
): { tick: (input: HeadOfLineTickInput) => boolean; reset: () => void } => {
	// 目前正在追蹤等待的 index，尚未追蹤時為 null
	let trackedIndex: number | null = null
	// 目前追蹤 index 的起算時間戳
	let trackedSinceMs = 0

	return {
		tick: (input: HeadOfLineTickInput): boolean => {
			// 沒有合法 index 或音檔已就緒，清除追蹤並回報不需跳過
			if (input.index === null || input.hasAudioUrl) {
				trackedIndex = null
				return false
			}

			// index 改變時重新起算，不沿用舊的起算時間
			if (trackedIndex !== input.index) {
				trackedIndex = input.index
				trackedSinceMs = input.nowMs
				return false
			}

			// 尚未達到逾時門檻，繼續等待
			if (input.nowMs - trackedSinceMs < timeoutMs) return false

			// 已逾時，清除追蹤讓下一個 index（或呼叫端前進後的同一 index）重新起算，回報應該跳過
			trackedIndex = null
			return true
		},
		reset: (): void => {
			// 回前景時呼叫，清掉背景期間卡住的追蹤起點，避免延續已過期的逾時判定
			trackedIndex = null
			trackedSinceMs = 0
		},
	}
}

/**
 * @interface {StuckRecoveryInput} 卡死自癒動作判定的輸入快照
 */
export interface StuckRecoveryInput {
	/**
	 * 目前是否已設定音源
	 */
	hasSource: boolean
	/**
	 * <audio> 元素目前是否為 ended
	 */
	playerEnded: boolean
	/**
	 * <audio> 元素的 currentTime 是否已開始推進（大於 0）
	 */
	currentTimeStarted: boolean
}

/**
 * 依看門狗偵測到卡死當下的 player 狀態，判定應該原地續播還是把旗標交還輪詢排程；
 * 有音源、未播畢、且播放進度已開始推進時代表只是被 OS 暫停，原地 resume 比重讀整句更省流量也不打斷語意
 *
 * @param input {StuckRecoveryInput} 看門狗偵測到卡死當下的音源 / 播畢 / 播放進度狀態
 * @returns {'resume-play' | 'reset-flag'} resume-play 表示應該原地呼叫 play() 續播；reset-flag 表示應該重置播放中旗標交還輪詢排程
 *
 * @example
 * const action = buildStuckRecoveryAction({ hasSource: true, playerEnded: false, currentTimeStarted: true })
 * if (action === 'resume-play') audioPlayer.value.play()
 */
export const buildStuckRecoveryAction = (
	input: StuckRecoveryInput,
): 'resume-play' | 'reset-flag' => {
	// 有音源、未播畢、且播放進度已開始推進：只是被 OS 暫停，原地續播不重讀不重播整句
	if (input.hasSource && !input.playerEnded && input.currentTimeStarted) {
		return 'resume-play'
	}

	// 其餘情況（無音源 / 已播畢 / 播放進度尚未開始推進）：交還輪詢排程處理
	return 'reset-flag'
}
